import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import '../../styles/GoogleSignInButton.css';

let googleScriptPromise = null;

const loadGoogleIdentityScript = () => {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (googleScriptPromise) return googleScriptPromise;

  googleScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-abraliber-google-identity]');
    if (existing) {
      existing.addEventListener('load', resolve, { once: true });
      existing.addEventListener('error', reject, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client?hl=pt-BR';
    script.async = true;
    script.defer = true;
    script.dataset.abraliberGoogleIdentity = 'true';
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });

  return googleScriptPromise;
};

const GoogleSignInButton = ({ onCredential, onError, disabled = false, dividerLabel = '' }) => {
  const containerRef = useRef(null);
  const credentialHandlerRef = useRef(onCredential);
  const errorHandlerRef = useRef(onError);
  const [clientId, setClientId] = useState('');
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    credentialHandlerRef.current = onCredential;
    errorHandlerRef.current = onError;
  }, [onCredential, onError]);

  useEffect(() => {
    let active = true;
    Promise.resolve(axios.get(`${API_BASE_URL}/config/auth`))
      .then((response) => {
        const configuredClientId = response?.data?.google_client_id;
        if (active && response?.data?.google_enabled && configuredClientId) {
          setClientId(configuredClientId);
        }
      })
      .catch(() => {
        if (active) setClientId('');
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!clientId || !containerRef.current) return undefined;
    let active = true;

    loadGoogleIdentityScript()
      .then(() => {
        if (!active || !containerRef.current || !window.google?.accounts?.id) return;

        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (response) => {
            if (response?.credential) {
              credentialHandlerRef.current?.(response.credential);
            } else {
              errorHandlerRef.current?.('O Google não retornou uma credencial válida.');
            }
          },
          auto_select: false,
          cancel_on_tap_outside: true,
        });

        containerRef.current.replaceChildren();
        const measuredWidth = Math.floor(
          containerRef.current.getBoundingClientRect().width
          || containerRef.current.parentElement?.getBoundingClientRect().width
          || Math.max(window.innerWidth - 48, 120)
        );
        window.google.accounts.id.renderButton(containerRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'rectangular',
          logo_alignment: 'left',
          width: Math.max(120, Math.min(measuredWidth - 2, 320)),
          locale: 'pt-BR',
        });
        setAvailable(true);
      })
      .catch(() => {
        if (active) {
          setAvailable(false);
          errorHandlerRef.current?.('Não foi possível carregar o acesso com Google.');
        }
      });

    return () => {
      active = false;
    };
  }, [clientId]);

  if (!clientId) return null;

  return (
    <>
      <div
        className={`google-signin-wrap ${disabled ? 'is-disabled' : ''}`}
        aria-busy={!available || disabled}
      >
        <div ref={containerRef} className="google-signin-button" />
        {disabled && <span className="google-signin-blocker" aria-hidden="true" />}
      </div>
      {dividerLabel && (
        <div className="auth-provider-divider"><span>{dividerLabel}</span></div>
      )}
    </>
  );
};

export default GoogleSignInButton;
